import React, { useState, useRef, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, query, where, getDocs, addDoc, deleteDoc, updateDoc, doc } from "firebase/firestore/lite";
import { auth, db } from "../../firebase";
import { LanguageContext } from "../../context/LanguageContext";
import lang from "../../components/translations";

const LanguageAndAttachment = ({ nextStep, prevStep, languageSkills, personalInfo, handleDataChange }) => {
  const [cv, setCv] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [lastAddedId, setLastAddedId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { appLanguage } = useContext(LanguageContext);

  const [originalData, setOriginalData] = useState(null); // Store the original data

  useEffect(() => {
    if (!originalData && languageSkills) {
      setOriginalData({ ...languageSkills }); // Store the original data on mount
    }
  }, [languageSkills, originalData]);

  const skillLevels = ["Beginner", "Good", "Very Good", "Fluent"];

  const inputRef = useRef(null);

  // Rows are edited inline — every keystroke/selection saves straight into the wizard state.
  const updateRow = (lId, field, value) => {
    handleDataChange(
      languageSkills.map((l) => (l.lId === lId ? { ...l, [field]: value } : l)),
      "languageSkills"
    );
  };

  const addLanguage = () => {
    const newRow = {
      lId: Date.now().toString(),
      name: "",
      reading: "Beginner",
      writing: "Beginner",
      speaking: "Beginner",
      listening: "Beginner",
    };
    handleDataChange([...languageSkills, newRow], "languageSkills");
    setLastAddedId(newRow.lId);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 250);
  };

  const deleteLanguage = (id) => {
    try {
      const updatedLanguageSkills = languageSkills.filter((language) => language.lId !== id);
      handleDataChange(updatedLanguageSkills, "languageSkills");
    } catch (error) {
      console.error("Error deleting language: ", error);
    }
  };

  const allowedFileTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const maxFileSize = 5 * 1024 * 1024; // 5 MB

  const validateFile = (file) => {
    if (!allowedFileTypes.includes(file.type)) {
      setError(lang[appLanguage].errorFileInvild);
      return false;
    }

    if (file.size > maxFileSize) {
      setError(lang[appLanguage].errorFileSize);
      return false;
    }

    setError("");
    return true;
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (validateFile(file)) {
      if (name === "cv") {
        setCv(file);
      }
    } else {
      if (name === "cv") {
        setCv(null);
      }
    }
  };

  // Drag & drop onto the upload zone — same validation path as the file picker
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    setCv(validateFile(file) ? file : null);
  };

  const clearSelectedCv = () => {
    setCv(null);
    const input = document.getElementById("cvInput");
    if (input) input.value = "";
  };

  // Icon + color by file extension for the file cards
  const fileIcon = (name = "") => {
    const n = String(name).toLowerCase();
    if (n.endsWith(".pdf")) return "bi-file-earmark-pdf text-danger";
    if (n.endsWith(".doc") || n.endsWith(".docx")) return "bi-file-earmark-word text-primary";
    if (/\.(jpe?g|png)$/.test(n)) return "bi-file-earmark-image text-warning";
    return "bi-file-earmark text-secondary";
  };

  const showToast = (header, message, time) => {
    const toastElement = document.getElementById("liveToast");
    const toastHeader = toastElement.querySelector(".me-auto");
    const toastBody = toastElement.querySelector(".toast-body");
    toastHeader.textContent = header;
    toastBody.textContent = message;
    const toast = new window.bootstrap.Toast(toastElement, {
      delay: time ? time : 5000, // one minute
    });
    toast.show();
  };

  const handleSaveAndContinue = async () => {
    if (error) {
      showToast(lang[appLanguage].warnWaitTitle, lang[appLanguage].warnFixErrs);
      return;
    }

    if (!(languageSkills.length > 0)) {
      showToast(lang[appLanguage].warnAddLangTitle, lang[appLanguage].warmAddOneLang);
      return;
    }

    for (const l of languageSkills) {
      if (!l.name || l.name.trim() === "" || l.name === "Write a Language") {
        showToast(lang[appLanguage].errorAddLangTitle, lang[appLanguage].errorAddLangNameEmpty, 10000);
        return;
      }
    }

    if (!personalInfo.cvURL && !cv) {
      showToast(lang[appLanguage].warnWaitTitle, lang[appLanguage].warmAddOneAtch);
      return;
    }

    try {
      setUploading(true);
      const user = auth.currentUser;
      const userEmail = user.email;

      // Query Firebase for the user's personalInfo document
      const personalInfoQuery = query(collection(db, "personalInfo"), where("email", "==", userEmail));
      const querySnapshot = await getDocs(personalInfoQuery);

      if (!querySnapshot.empty) {
        const personalInfoDoc = querySnapshot.docs[0];

        const hasChanged = JSON.stringify(originalData) !== JSON.stringify(languageSkills);

        if (hasChanged) {
          await saveLanguageSkills(personalInfoDoc); // Handle languages
        }
        await saveAttachments(personalInfoDoc); // Handle CV/attachments

        setUploading(false);
        nextStep();
      } else {
        showToast(lang[appLanguage].errorTitle, lang[appLanguage].errorPiNotFond);
        setUploading(false);
      }
    } catch (error) {
      setUploading(false);
    }
  };

  const saveLanguageSkills = async (personalInfoDoc) => {
    try {
      // Fetch current language skills from Firebase to compare with local state
      const languageSnapshot = await getDocs(collection(db, "personalInfo", personalInfoDoc.id, "languageSkills"));
      const firebaseLanguageIds = languageSnapshot.docs.map((doc) => doc.id); // Get all current Firebase document IDs

      // Track local document IDs
      const localLanguageIds = languageSkills.map((entry) => entry.lId).filter(Boolean);

      // Delete any documents in Firebase that are not in the local languageSkills array
      for (const firebaseDocId of firebaseLanguageIds) {
        if (!localLanguageIds.includes(firebaseDocId)) {
          const docRef = doc(db, "personalInfo", personalInfoDoc.id, "languageSkills", firebaseDocId);
          await deleteDoc(docRef); // Delete from Firebase
        }
      }

      // Now, update or add documents to Firebase
      for (const entry of languageSkills) {
        if (entry.lId && firebaseLanguageIds.includes(entry.lId)) {
          // If the document ID exists locally and is also in Firestore, update it
          const languageDocRef = doc(db, "personalInfo", personalInfoDoc.id, "languageSkills", entry.lId);
          await updateDoc(languageDocRef, {
            name: entry.name,
            reading: entry.reading,
            writing: entry.writing,
            speaking: entry.speaking,
            listening: entry.listening,
          });
        } else {
          // If there's no lId (it's a new document), add it
          const newDocRef = await addDoc(collection(db, "personalInfo", personalInfoDoc.id, "languageSkills"), {
            name: entry.name,
            reading: entry.reading,
            writing: entry.writing,
            speaking: entry.speaking,
            listening: entry.listening,
          });

          // Attach the generated Firestore ID to the local entry (but not to Firestore)
          entry.lId = newDocRef.id;
        }
      }
    } catch (error) {
      console.error("Error saving language skills to Firebase:", error);
      throw error; // Re-throw the error to be handled in handleSaveAndContinue
    }
  };

  const saveAttachments = async (personalInfoDoc) => {
    try {
      const updates = {};

      if (cv) {
        let fileName = cv.name.split(".").slice(0, -1).join(".");

        // If cv.name is valid (english aphabet with - ) then replace space with _ and set as cv name
        // Else If user fullname is valid then replace space with _ and set as cv name
        // Else set user Id as cv name
        fileName = /^[A-Za-z\s-]+$/.test(fileName)
          ? fileName.replace(/\s+/g, "_") + cv.name.match(/\.[^/.]+$/)?.[0]
          : /^[A-Za-z\s-]+$/.test(personalInfo.fullName)
            ? personalInfo.fullName.replace(/\s+/g, "_") + cv.name.match(/\.[^/.]+$/)?.[0]
            : personalInfo.id;

        // Call uploadFile with the validated/modified fileName
        const cvURL = await uploadFile(cv, `cv/${personalInfoDoc.id}/${fileName}`);

        //const cvURL = await uploadFile(cv, `cv/${personalInfoDoc.id}/${cv.name}`);
        updates.cvURL = cvURL;
        updates.cvName = cv.name;
        personalInfo.cvName = cv.name;
        personalInfo.cvURL = cvURL;
        const personalInfoDocRef = doc(db, "personalInfo", personalInfoDoc.id);
        await updateDoc(personalInfoDocRef, updates);
      }
    } catch (error) {
      showToast(lang[appLanguage].errorTitle, lang[appLanguage].errorUpldFile + error.message);
      throw error;
    }
  };

  const uploadFile = async (file, filePath) => {
    try {
      if (!filePath) {
        console.error("File path is undefined or null");
        return;
      }

      // Initialize Firebase Storage
      const storage = getStorage();

      // Get a reference to the storage location
      const storageRef = ref(storage, filePath);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error; // Rethrow the error for further handling
    }
  };

  const handleDeleteFile = async (fileFieldURL, fileFieldName) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userEmail = user.email;
        const personalInfoQuery = query(collection(db, "personalInfo"), where("email", "==", userEmail));
        const querySnapshot = await getDocs(personalInfoQuery);

        if (!querySnapshot.empty) {
          const personalInfoDoc = querySnapshot.docs[0];
          const personalInfoDocRef = doc(db, "personalInfo", personalInfoDoc.id);

          // Update the document in Firebase
          await updateDoc(personalInfoDocRef, {
            [fileFieldURL]: null,
            [fileFieldName]: null,
          });

          // Update the personalInfo in local state using handleDataChange
          const updatedPersonalInfo = {
            ...personalInfo,
            [fileFieldURL]: null,
            [fileFieldName]: null,
          };

          // Call handleDataChange to pass updated personalInfo back to the parent component
          handleDataChange(updatedPersonalInfo, "personalInfo");
        }
      }
    } catch (error) {
      showToast(lang[appLanguage].errorTitle, lang[appLanguage].errorDltFile + error.message);
    }
  };

  return (
    <div className="container">
      <p className="text-muted">{lang[appLanguage].lsaDisc1}</p>
      <p className="text-muted">{lang[appLanguage].lsaDisc2}</p>
      <p className="text-muted">{lang[appLanguage].lsaDisc3}</p>
      <hr />
      <div className="mb-4 mx-0 navbar rounded-2 row shadow">
        <div className="col-12 col-md-6">
          <h4>
            <i className="bi bi-translate me-2"></i> {lang[appLanguage].lsaLangTitle}
          </h4>
        </div>
        <div className="col-12 col-md-6">
          <button
            type="button"
            className="btn btn-light float-end"
            disabled={uploading || error || languageSkills.some((l) => !l.name || !l.name.trim())}
            onClick={addLanguage}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {lang[appLanguage].btnAddLang}
          </button>
        </div>
      </div>

      <div className="row mb-5">
        <div className="col table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col" className="text-center">
                  {lang[appLanguage].lsaLangAction}
                </th>
                <th scope="col">{lang[appLanguage].lsaLang}</th>
                <th scope="col" className="text-center">
                  {lang[appLanguage].lsaLangRead}
                </th>
                <th scope="col" className="text-center">
                  {lang[appLanguage].lsaLangWrit}
                </th>
                <th scope="col" className="text-center">
                  {lang[appLanguage].lsaLangSpek}
                </th>
                <th scope="col" className="text-center">
                  {lang[appLanguage].lsaLangLstn}
                </th>
              </tr>
            </thead>
            <tbody>
              {languageSkills.map((language) => (
                <tr key={language.lId} className="fade-in">
                  <td className="text-center">
                    <button onClick={() => deleteLanguage(language.lId)} className="btn btn-danger btn-sm m-1">
                      <i className="bi bi-trash3"></i>
                    </button>
                  </td>
                  <th scope="row">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ex: Arabic or English ..."
                      value={language.name}
                      maxLength={50}
                      onChange={(e) => updateRow(language.lId, "name", e.target.value)}
                      ref={language.lId === lastAddedId ? inputRef : null}
                    />
                  </th>
                  {["reading", "writing", "speaking", "listening"].map((skill) => (
                    <td className="text-center" key={skill}>
                      <select
                        className="form-select"
                        value={language[skill]}
                        onChange={(e) => updateRow(language.lId, skill, e.target.value)}
                      >
                        {skillLevels.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-4 mx-0 navbar rounded-2 row shadow">
        <div className="col-12 col-md-6">
          <h4>
            <i className="bi bi-paperclip"></i> {lang[appLanguage].lsaAtchTitle}
          </h4>
        </div>
      </div>

      <div className="row mb-2">
        <div className="col">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-3">
            <label className="form-label fw-semibold">{lang[appLanguage].lsaUpldCV}</label>
            <label
              htmlFor="cvInput"
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              className={`d-block text-center rounded-3 p-4 w-100 ${
                isDragOver ? "bg-success bg-opacity-10" : "bg-light"
              }`}
              style={{
                border: `2px dashed ${isDragOver ? "#198754" : "#c2cccc"}`,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <i className="bi bi-cloud-arrow-up fs-1 text-success d-block"></i>
              <div className="fw-medium mt-1">{lang[appLanguage].lsaDropHint}</div>
              <div className="small text-muted mt-1">{lang[appLanguage].lsaDropTypes}</div>
            </label>
            <input
              type="file"
              className="d-none"
              id="cvInput"
              name="cv"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
          </div>

          {cv && (
            <div className="card border-success border-opacity-50 shadow-sm mb-3 fade-in">
              <div className="card-body py-2 d-flex align-items-center">
                <i className={`bi ${fileIcon(cv.name)} fs-2 me-3`}></i>
                <div className="flex-grow-1">
                  <div className="fw-medium">{cv.name}</div>
                  <div className="small text-muted">
                    {(cv.size / 1024 / 1024).toFixed(2)} MB · {lang[appLanguage].lsaSelectedFile}
                  </div>
                </div>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={clearSelectedCv}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          )}

          {personalInfo.cvURL && (
            <div className="card shadow-sm mb-3 fade-in">
              <div className="card-body py-2 d-flex align-items-center">
                <i className={`bi ${fileIcon(personalInfo.cvName)} fs-2 me-3`}></i>
                <div className="flex-grow-1">
                  <div className="fw-medium">{personalInfo.cvName || "CV"}</div>
                  <div className="small text-muted">{lang[appLanguage].lsaCurrentCV}</div>
                </div>
                <a
                  href={personalInfo.cvURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-success btn-sm me-2"
                >
                  <i className="bi bi-eye me-1"></i>
                  {lang[appLanguage].lsaBtnView}
                </a>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDeleteFile("cvURL", "cvName")}
                >
                  <i className="bi bi-trash3"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <button
            type="button"
            className="btn btn-success button me-1"
            onClick={prevStep}
            disabled={uploading || error}
          >
            {lang[appLanguage].btnBack}
          </button>
          <button
            type="button"
            className="btn btn-success button"
            onClick={handleSaveAndContinue}
            disabled={uploading || error}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                <span role="status"> {lang[appLanguage].lsaUploading}</span>
              </>
            ) : (
              lang[appLanguage].btnSaveCont
            )}{" "}
          </button>
        </div>
      </div>
    </div>
  );
};

LanguageAndAttachment.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  personalInfo: PropTypes.object.isRequired,
  languageSkills: PropTypes.array.isRequired,
  handleDataChange: PropTypes.func.isRequired,
};

export default LanguageAndAttachment;
